/**
 * Action Button Group Component
 * 
 * Displays action buttons for upload, download, and delete operations.
 * Buttons are disabled when prerequisites are not met.
 * 
 * @module ActionButtonGroup
 */

import React from 'react';
import { Box, Button, Tooltip } from '@mui/material';

import {
  primaryPillButtonSx,
  secondaryPillButtonSx,
  destructivePillButtonSx,
  drawerActionButtonSx,
  drawerActionRowSx,
} from '../../../../theme/componentStyles';
import { useTranslation } from 'react-i18next';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../../utils/iconFallbacks';
/**
 * Props for ActionButtonGroup component
 */
export interface ActionButtonGroupProps {
  /** Whether upload button should be enabled */
  canUpload: boolean;
  /** Whether download button should be enabled */
  canDownload: boolean;
  /** Whether delete button should be enabled */
  canDelete: boolean;
  /** Callback when upload is clicked */
  onUpload: () => void;
  /** Callback when download is clicked */
  onDownload: () => void;
  /** Callback when delete is clicked */
  onDelete: () => void;
  /** Whether any operation is in progress */
  isProcessing?: boolean;
  /** Tooltip message for upload button when disabled */
  uploadTooltip?: string;
  /** Tooltip message for download button when disabled */
  downloadTooltip?: string;
  /** Tooltip message for delete button when disabled */
  deleteTooltip?: string;
}

/**
 * ActionButtonGroup Component
 * 
 * Displays action buttons with appropriate disabled states and tooltips.
 */
export const ActionButtonGroup: React.FC<ActionButtonGroupProps> = ({
  canUpload,
  canDownload,
  canDelete,
  onUpload,
  onDownload,
  onDelete,
  isProcessing = false,
  uploadTooltip,
  downloadTooltip,
  deleteTooltip,
}) => {
  const { t } = useTranslation('mapTransfer');
  const prereqMessage = t('routes.section.actionButtons.prereqMessage');
  return (
    <Box
      sx={{
        ...drawerActionRowSx,
        gap: 1.5,
        width: '100%',
      }}
    >
      <Tooltip title={uploadTooltip || (canUpload ? t('routes.section.actionButtons.uploadDefault') : prereqMessage)}>
        <span>
          <Button
            variant="contained"
            startIcon={<Icon size={20} name="upload" />}
            onClick={onUpload}
            disabled={!canUpload || isProcessing}
            fullWidth
            data-testid="transfer.action.upload"
            sx={(theme) => ({
              ...primaryPillButtonSx(theme),
              ...drawerActionButtonSx,
            })}
          >
            {t('routes.section.actionButtons.upload')}
          </Button>
        </span>
      </Tooltip>

      <Tooltip title={downloadTooltip || (canDownload ? t('routes.section.actionButtons.downloadDefault') : prereqMessage)}>
        <span>
          <Button
            variant="outlined"
            startIcon={<Icon size={20} d={ICON_FALLBACKS.CloudDownload} />}
            onClick={onDownload}
            disabled={!canDownload || isProcessing}
            fullWidth
            data-testid="transfer.action.download"
            sx={(theme) => ({
              ...secondaryPillButtonSx(theme),
              ...drawerActionButtonSx,
            })}
          >
            {t('routes.section.actionButtons.download')}
          </Button>
        </span>
      </Tooltip>

      <Tooltip title={deleteTooltip || (canDelete ? t('routes.section.actionButtons.deleteDefault') : prereqMessage)}>
        <span>
          <Button
            variant="outlined"
            startIcon={<Icon size={20} name="trash" />}
            onClick={onDelete}
            disabled={!canDelete || isProcessing}
            fullWidth
            data-testid="transfer.action.delete"
            sx={(theme) => ({
              ...destructivePillButtonSx(theme),
              ...drawerActionButtonSx,
            })}
          >
            {t('routes.section.actionButtons.delete')}
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
};

